import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Divider, Dropdown, Menu, Row, Space, Table } from 'antd';
import type { MenuProps } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { TVendorCode } from '../../../models/vendor/VendorCodeModels';

import BulkVendorCodeCommercialModalForm from './../../vendor/forms/BulkVendorCodeCommercialModalForm';
import ROUTES from '../../../constants/routes';
import COLORS from '../../../constants/colors';

import type { TTableColumn } from './../../../utils/tableUtils';
import VendorCodeCommercialModalForm from './../../vendor/forms/VendorCodeCommercialModalForm';
import VendorCodeCommercialHomeWarrantyModalForm from './../../vendor/forms/VendorCodeCommercialHomeWarrantyModalForm';
import VendorCodeInternetModalForm from './../../vendor/forms/VendorCodeInternetModalForm';
import VendorCodeInternetHomeWarrantyModalForm from './../../vendor/forms/VendorCodeInternetHomeWarrantyModalForm';
import { VendorCodeType } from '../../../enums/vendor/VendorCodeTypeEnum';
import VendorCodeTypeText from '../displays/VendorCodeTypeText';
import { getBooleanDisplayValue } from './../../../utils/displayUtils';
import { getVendorCodeTypeDisplayValue } from './../../../utils/displayUtils';
import TableWithActionsColumn from '../../hocs/tables/TableWithActionsColumn';
import TableWithHeader from '../../hocs/tables/TableWithHeader';
import TextSearchColumnFilter from '../../shared/columnFilters/TextSearchColumnFilter';

import { uniqBy } from 'lodash';

import './../../../css/vendor/tables/VendorCodesTable.css';

type Props = {
  data: Array<TVendorCode>;
  isLoading?: boolean;
  vendorId?: number;
  vendorCodeTypes?: Array<VendorCodeType>;
};

const ActionableTableWithHeader = TableWithHeader(TableWithActionsColumn(Table));
const ALL_VENDOR_CODE_TYPES = [
  VendorCodeType.COMMERCIAL,
  VendorCodeType.INTERNET,
  VendorCodeType.COMMERCIAL_HOME_WARRANTY,
  VendorCodeType.INTERNET_HOME_WARRANTY,
];

const VendorCodesTable = ({ data, isLoading = false, vendorId, vendorCodeTypes }: Props) => {
  const navigate = useNavigate();
  const shouldShowVendorName = !vendorId;
  const allowedVendorCodeTypes = useMemo(
    () => vendorCodeTypes ?? ALL_VENDOR_CODE_TYPES,
    [vendorCodeTypes],
  );
  const filteredData = useMemo(
    () => data.filter((vendorCode) => allowedVendorCodeTypes.includes(vendorCode.type)),
    [allowedVendorCodeTypes, data],
  );

  const [selectedVendorCode, setSelectedVendorCode] = useState<any>(null);
  const [isModalShown, setIsModalShown] = useState<VendorCodeType | boolean>(false);
  const [isBulkUpdateModalShown, setIsBulkUpdateModalShown] = useState(false);
  const [bulkVendorCodeType, setBulkVendorCodeType] = useState<
    VendorCodeType.COMMERCIAL | VendorCodeType.COMMERCIAL_HOME_WARRANTY
  >(VendorCodeType.COMMERCIAL);
  const [filteredDataLength, setFilteredDataLength] = useState<number>(0);

  const onUpdate = useCallback((record: TVendorCode) => {
    setSelectedVendorCode(record);
    setIsModalShown(record.type);
  }, []);

  const columns = useMemo(
    () => getColumns(filteredData, shouldShowVendorName, onUpdate, allowedVendorCodeTypes),
    [allowedVendorCodeTypes, filteredData, onUpdate, shouldShowVendorName],
  );

  const onCreate = useCallback(({ key }) => {
    setSelectedVendorCode(null);
    setIsModalShown(key);
  }, []);
  const createMenu = getCreateMenu(onCreate, allowedVendorCodeTypes);
  const onBulkUpdate = useCallback(({ key }) => {
    setBulkVendorCodeType(
      key as VendorCodeType.COMMERCIAL | VendorCodeType.COMMERCIAL_HOME_WARRANTY,
    );
    setIsBulkUpdateModalShown(true);
  }, []);
  const bulkUpdateMenu = getBulkUpdateMenu(onBulkUpdate, allowedVendorCodeTypes);
  const shouldShowBulkUpdate = allowedVendorCodeTypes.some((vendorCodeType) =>
    [VendorCodeType.COMMERCIAL, VendorCodeType.COMMERCIAL_HOME_WARRANTY].includes(vendorCodeType),
  );

  const getFooter = (vendorCodes: Array<TVendorCode>) => {
    const countOfCommercialVendorCodes = vendorCodes.filter(
      (vendorCode) => vendorCode.type === VendorCodeType.COMMERCIAL,
    ).length;
    const countOfCommercialHomeWarrantyVendorCodes = vendorCodes.filter(
      (vendorCode) => vendorCode.type === VendorCodeType.COMMERCIAL_HOME_WARRANTY,
    ).length;
    const countOfInternetVendorCodes = vendorCodes.filter(
      (vendorCode) => vendorCode.type === VendorCodeType.INTERNET,
    ).length;
    const countOfInternetHomeWarrantyVendorCodes = vendorCodes.filter(
      (vendorCode) => vendorCode.type === VendorCodeType.INTERNET_HOME_WARRANTY,
    ).length;
    const countOfAllVendorCodes = vendorCodes.length;

    return (
      <div className="vendorCodesTableFooter">
        <Space split={<Divider type="vertical" />}>
          {countOfCommercialVendorCodes > 0 && (
            <Badge
              color={COLORS.VENDORS_TFN_PILL}
              text={`${getVendorCodeTypeDisplayValue(VendorCodeType.COMMERCIAL)}
              (${countOfCommercialVendorCodes})`}
            />
          )}
          {countOfInternetVendorCodes > 0 && (
            <Badge
              color={COLORS.VENDORS_INTERNET_PILL}
              text={`${getVendorCodeTypeDisplayValue(VendorCodeType.INTERNET)}
              (${countOfInternetVendorCodes})`}
            />
          )}
          {countOfCommercialHomeWarrantyVendorCodes > 0 && (
            <Badge
              color={COLORS.VENDORS_TFN_PILL}
              text={`${getVendorCodeTypeDisplayValue(VendorCodeType.COMMERCIAL_HOME_WARRANTY)}
              (${countOfCommercialHomeWarrantyVendorCodes})`}
            />
          )}
          {countOfInternetHomeWarrantyVendorCodes > 0 && (
            <Badge
              color={COLORS.VENDORS_INTERNET_PILL}
              text={`${getVendorCodeTypeDisplayValue(VendorCodeType.INTERNET_HOME_WARRANTY)}
              (${countOfInternetHomeWarrantyVendorCodes})`}
            />
          )}
          {countOfAllVendorCodes > 0 && <span>Total ({countOfAllVendorCodes})</span>}
        </Space>
      </div>
    );
  };

  const tableHeader = (
    <Row key="actionBar" justify="end">
      <Space>
        {shouldShowBulkUpdate && (
          <Dropdown overlay={bulkUpdateMenu}>
            <Button type="primary">Bulk Update</Button>
          </Dropdown>
        )}
        <Dropdown overlay={createMenu}>
          <Button type="primary">Create Vendor Code</Button>
        </Dropdown>
      </Space>
    </Row>
  );

  return (
    <div className="vendorCodesTableRoot vendorMarginTopOffset20">
      <ActionableTableWithHeader
        header={tableHeader}
        bordered
        columns={columns}
        onChange={(pagination, filters, sorter, extra) => {
          setFilteredDataLength(extra.currentDataSource.length);
        }}
        dataSource={filteredData}
        loading={isLoading}
        onRow={(record) => ({
          onDoubleClick: () => navigate(getVendorCodeDetailRoute(record)),
        })}
        size="small"
        rowKey={(record) => `${record.type}-${record.id}`}
        footer={getFooter}
        pagination={{
          hideOnSinglePage: false,
          responsive: true,
          total: filteredDataLength,
          showSizeChanger: true,
          defaultPageSize: 100,
          position: ['topRight', 'bottomRight'],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
      />
      <VendorCodeCommercialModalForm
        initialData={selectedVendorCode}
        isShown={isModalShown === VendorCodeType.COMMERCIAL}
        setIsShown={setIsModalShown}
        vendorId={vendorId}
      />
      <BulkVendorCodeCommercialModalForm
        isShown={isBulkUpdateModalShown}
        setIsShown={setIsBulkUpdateModalShown}
        vendorCodeType={bulkVendorCodeType}
      />
      <VendorCodeInternetModalForm
        initialData={selectedVendorCode}
        isShown={isModalShown === VendorCodeType.INTERNET}
        setIsShown={setIsModalShown}
        vendorId={vendorId}
      />
      <VendorCodeCommercialHomeWarrantyModalForm
        initialData={selectedVendorCode}
        isShown={isModalShown === VendorCodeType.COMMERCIAL_HOME_WARRANTY}
        setIsShown={setIsModalShown}
        vendorId={vendorId}
      />
      <VendorCodeInternetHomeWarrantyModalForm
        initialData={selectedVendorCode}
        isShown={isModalShown === VendorCodeType.INTERNET_HOME_WARRANTY}
        setIsShown={setIsModalShown}
        vendorId={vendorId}
      />
    </div>
  );
};

function getColumns(
  data: Array<TVendorCode>,
  shouldShowVendorName: boolean,
  onUpdate: (record: TVendorCode) => void,
  vendorCodeTypes: Array<VendorCodeType>,
): Array<TTableColumn | null> {
  return [
    {
      title: 'Type',
      dataIndex: 'type',
      render: (value) => <VendorCodeTypeText status={value} />,
      sorter: (a, b) => a.type.localeCompare(b.type),
      filterSearch: true,
      filters: vendorCodeTypes.map((vcType) => ({
        text: getVendorCodeTypeDisplayValue(vcType),
        value: vcType,
      })),
      onFilter: (vcType: VendorCodeType, record: TVendorCode): boolean => record.type === vcType,
    },
    {
      title: 'Code',
      dataIndex: 'legacyVendorId',
      render: (value, row) => <Link to={getVendorCodeDetailRoute(row)}>{value}</Link>,
      sorter: (a, b) => a.legacyVendorId.localeCompare(b.legacyVendorId),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <TextSearchColumnFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        return record.legacyVendorId
          .toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase());
      },
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? COLORS.VENDORS_SEARCH_ICON_ACTIVE : undefined }}
        />
      ),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <TextSearchColumnFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      onFilter: (value, record) => {
        return record.name.toString().toLowerCase().includes(value.toString().toLowerCase());
      },
      filterIcon: (filtered) => (
        <SearchOutlined
          style={{ color: filtered ? COLORS.VENDORS_SEARCH_ICON_ACTIVE : undefined }}
        />
      ),
    },
    shouldShowVendorName
      ? {
          title: 'Vendor',
          dataIndex: 'vendorName',
          render: (value, row) => <Link to={`${ROUTES.VENDORS}/${row.vendorId}`}>{value}</Link>,
          sorter: (a, b) => a.vendorName.localeCompare(b.vendorName),
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <TextSearchColumnFilter
              setSelectedKeys={setSelectedKeys}
              selectedKeys={selectedKeys}
              confirm={confirm}
              clearFilters={clearFilters}
            />
          ),
          onFilter: (value, record) => {
            return record.vendorName
              .toString()
              .toLowerCase()
              .includes(value.toString().toLowerCase());
          },
          filterIcon: (filtered) => (
            <SearchOutlined
              style={{ color: filtered ? COLORS.VENDORS_SEARCH_ICON_ACTIVE : undefined }}
            />
          ),
        }
      : null,
    {
      title: 'Tier',
      dataIndex: 'marketingTier',
      render: (value) => value,
      sorter: (a, b) => a.marketingTier - b.marketingTier,
      filterSearch: true,
      filters: uniqBy(data, 'marketingTier')
        .sort((a, b) => a.marketingTier - b.marketingTier)
        .map((record) => ({
          text: record.marketingTier,
          value: record.marketingTier,
        })),
      onFilter: (searchValue: number, record: TVendorCode): boolean =>
        searchValue === record.marketingTier,
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: getBooleanDisplayValue,
      sorter: (a, b) => a.isActive - b.isActive,
      filterSearch: true,
      filters: [true, false].map((tfnType) => ({
        text: getBooleanDisplayValue(tfnType),
        value: tfnType,
      })),
      onFilter: (isActive: boolean, record: TVendorCode): boolean => record.isActive === isActive,
      defaultFilteredValue: ['true'],
    },
    {
      title: 'Actions',
      key: 'action',
      render: (text, record) => (
        <div>
          <Button type="primary" onClick={() => onUpdate(record)}>
            Update
          </Button>
        </div>
      ),
    },
  ];
}

function getCreateMenu(onCreate: (key) => void, vendorCodeTypes: Array<VendorCodeType>): any {
  const menuItems: MenuProps['items'] = [
    {
      key: VendorCodeType.COMMERCIAL,
      label: 'TFN Only',
    },
    {
      key: VendorCodeType.INTERNET,
      label: 'Internet',
    },
    {
      key: VendorCodeType.COMMERCIAL_HOME_WARRANTY,
      label: 'TFN Only - Home Warranty',
    },
    {
      key: VendorCodeType.INTERNET_HOME_WARRANTY,
      label: 'Internet - Home Warranty',
    },
  ].filter((item) => vendorCodeTypes.includes(item?.key as VendorCodeType));
  return <Menu onClick={onCreate} items={menuItems} />;
}

function getBulkUpdateMenu(
  onBulkUpdate: (key) => void,
  vendorCodeTypes: Array<VendorCodeType>,
): any {
  const menuItems: MenuProps['items'] = [
    {
      key: VendorCodeType.COMMERCIAL,
      label: 'Commercial',
    },
    {
      key: VendorCodeType.COMMERCIAL_HOME_WARRANTY,
      label: 'Commercial Home Warranty',
    },
  ].filter((item) => vendorCodeTypes.includes(item?.key as VendorCodeType));

  return <Menu onClick={onBulkUpdate} items={menuItems} />;
}

function getVendorCodeDetailRoute(vendorCode: TVendorCode): string {
  if (vendorCode.type === VendorCodeType.COMMERCIAL) {
    return `${ROUTES.VENDOR_CODES_COMMERCIAL}/${vendorCode.id}`;
  }
  if (vendorCode.type === VendorCodeType.COMMERCIAL_HOME_WARRANTY) {
    return `${ROUTES.VENDOR_CODES_COMMERCIAL_HOME_WARRANTY}/${vendorCode.id}`;
  }
  if (vendorCode.type === VendorCodeType.INTERNET_HOME_WARRANTY) {
    return `${ROUTES.VENDOR_CODES_INTERNET_HOME_WARRANTY}/${vendorCode.id}`;
  }
  return `${ROUTES.VENDOR_CODES_INTERNET}/${vendorCode.id}`;
}

export default VendorCodesTable;
